<template>
    <div
        v-if="type"
        :class="[
            'book-now-pay-later',
            { 'margin-top': marginTop },
            { 'margin-bottom': marginBottom },
        ]">
        <Asset :type="assetType" class="book-now-pay-later-logo-small" />
        <span class="book-now-pay-later-content">
            <span>
                <strong>{{ t({ key: 'book_now_pay_later.book_now' }) }}</strong>
                -
                {{ t({ key: 'book_now_pay_later.pay_later' }) }}</span
            >

            <ButtonLink :onClick="toggleModal">
                {{ t({ key: 'shared.read_more' }) }}
            </ButtonLink>
        </span>

        <Modal
            v-if="modal"
            :onClose="toggleModal"
            :title="`${t({ key: 'book_now_pay_later.book_now' })} - ${t({ key: 'book_now_pay_later.pay_later' })}`"
            size="m">
            <div class="book-now-pay-later-modal">
                <p v-if="modalDescription">{{ modalDescription }}</p>

                <ul class="book-now-pay-later-steps">
                    <li
                        v-for="(item, index) in modalSteps"
                        :key="item.step"
                        class="book-now-pay-later-step">
                        <div
                            v-if="index < modalSteps.length - 1"
                            class="book-now-pay-later-line" />
                        <span class="book-now-pay-later-step-number">
                            {{ item.step }}
                        </span>
                        <span class="book-now-pay-later-step-text">
                            {{ item.label }}
                        </span>
                    </li>
                </ul>

                <Asset :type="assetType" class="book-now-pay-later-logo-large" />
                <BookNowPayLaterWarning />
            </div>

            <template #footer>
                <div class="book-now-pay-later-footer">
                    <ButtonPrimary :onClick="toggleModal">
                        {{ t({ key: 'shared.close', format: LabelFormat.Capitalize }) }}
                    </ButtonPrimary>
                </div>
            </template>
        </Modal>
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { defineComponent, computed, ref, watch } from 'vue';
    import { useStore } from '@/store';
    import { Market } from '@shared/config/market/market.models';
    import {
        getAsset,
        getName,
        getModalDescription,
        getType,
        getModalSteps,
    } from './BookNowPayLater.helpers';
    import { BookNowPayLaterType, BookNowPayLaterStep } from './BookNowPayLater.interfaces';
    import { LabelFormat } from '@shared/interfaces/translation';
    import { Type as AssetType } from '@ui/components/Asset/Asset.interfaces';
    import {
        logStatsigBookNowPayLaterAvailable,
        logStatsigBookNowPayLaterInteraction,
    } from '@/tracking/statsig/payment.functions';
    import { Cart } from '@/api/Cart/cart.interfaces';
    import Asset from '@ui/components/Asset/Asset.vue';
    import ButtonLink from '@ui/components/Button/ButtonLink.vue';
    import ButtonPrimary from '@ui/components/Button/ButtonPrimary.vue';
    import Modal from '@ui/components/Modal/Modal.vue';
    import BookNowPayLaterWarning from '@/components/shared/BookNowPayLaterWarning/BookNowPayLaterWarning.vue';

    export default defineComponent({
        name: 'BookNowPayLater',
        components: { Asset, ButtonLink, ButtonPrimary, Modal, BookNowPayLaterWarning },
        methods: { t },
        props: {
            marginTop: { type: Boolean, default: true },
            marginBottom: { type: Boolean, default: false },
            skipValidation: { type: Boolean, default: false },
        },
        setup(props) {
            const store = useStore();

            const tracked = ref<boolean>(false);
            const modal = ref<boolean>(false);

            const cart = computed((): Cart => store.state.cart.data);
            const market = computed((): Market => store.getters['general/getMarket']);

            const type = computed<BookNowPayLaterType | undefined>(() =>
                getType(market?.value?.number, cart.value, props.skipValidation)
            );

            const assetType = computed<AssetType | undefined>(() => getAsset(type.value));
            const name = computed<string | undefined>(() => getName(type.value));

            const modalDescription = computed<string | undefined>(() =>
                getModalDescription(type.value)
            );

            const modalSteps = computed<BookNowPayLaterStep[]>(() =>
                getModalSteps(name.value)
            );

            const toggleModal = () => {
                modal.value = !modal.value;
            };

            const initialTracking = () => {
                if (!tracked.value && type.value) {
                    logStatsigBookNowPayLaterAvailable(type.value);
                    tracked.value = true;
                }
            };

            watch(
                type,
                (state) => {
                    if (state) {
                        initialTracking();
                    }
                },
                { immediate: true }
            );

            watch(
                modal,
                (state) => {
                    if (state) {
                        logStatsigBookNowPayLaterInteraction(type.value, 'ReadMore');
                    }
                },
                { immediate: true }
            );

            return {
                type,
                assetType,
                toggleModal,
                modal,
                modalSteps,
                modalDescription,
                LabelFormat,
            };
        },
    });
</script>

<style scoped lang="scss">
    @forward 'BookNowPayLater';
</style>
